import React, { useState, useEffect, useMemo } from 'react';
import printObituaryReview from '../utils/printObituaryReview';

/**
 * Step-by-step obituary wizard — same data shape as <ObituaryForm/>,
 * presented as a guided, one-question-at-a-time experience.
 *
 * Props mirror ObituaryForm for drop-in compatibility:
 *   - customSubmit(formData) -> Promise<{ obituary_text, credits_remaining, ... }>
 *   - onGenerate(payload)
 *   - initialFormData (optional)
 *   - loading (externalLoading)
 */

const EMPTY_DATA = {
  firstName: '',
  middleName: '',
  lastName: '',
  nickname: '',
  dateOfBirth: '',
  dateOfDeath: '',
  age: '',
  homeCity: '',
  homeState: '',
  birthplace: '',
  occupation: '',
  education: '',
  militaryService: '',
  hobbies: '',
  achievements: '',
  survivedBy: '',
  precededBy: '',
  serviceDate: '',
  serviceLocation: '',
  favoriteQuote: '',
  personalMessage: '',
};

// Steps for the wizard. Each step can include:
//   - prompts: array of suggested questions (one per field/idea) to read to
//     the family. Use {name} as a placeholder — it will be replaced with the
//     deceased's first name if known, or "your loved one" otherwise.
//   - Each field may carry a `promptIndex` (1-based) so we can show a
//     matching [#] badge next to its label.
const STEPS = [
  {
    id: 'name',
    title: 'What was their name?',
    subtitle: "We'll use this throughout the obituary.",
    prompts: [
      "To start, I want to make sure we spell everything exactly the way {name}'s family knows it. Could you give me the full legal name — first, middle, and last?",
      "Did {name} go by a nickname or preferred name that family and friends used? We can include that in the obituary if you'd like.",
    ],
    fields: [
      { name: 'firstName',  label: 'First name',  placeholder: 'Jane',  required: true, col: 'col-md-4', promptIndex: 1 },
      { name: 'middleName', label: 'Middle name', placeholder: 'Ann',                 col: 'col-md-4', promptIndex: 1 },
      { name: 'lastName',   label: 'Last name',   placeholder: 'Doe',   required: true, col: 'col-md-4', promptIndex: 1 },
      { name: 'nickname',   label: 'Nickname / preferred name', placeholder: 'e.g., "Jack"', col: 'col-12', promptIndex: 2 },
    ],
    required: (d) => d.firstName.trim() && d.lastName.trim(),
  },
  {
    id: 'dates',
    title: 'When were they born and when did they pass?',
    subtitle: "If either date is unknown, leave it blank and just enter the age.",
    prompts: [
      "Could you share {name}'s date of birth? If it's uncertain, that's completely fine — we can use their age instead.",
      "And the date {name} passed away?",
      "If we need it: about how old was {name}? I'll just confirm the number with you.",
    ],
    customId: 'dates',
    required: (d) =>
      (d.dateOfBirth && d.dateOfDeath) || (d.age && String(d.age).trim() !== ''),
  },
  {
    id: 'home',
    title: 'Where did they live?',
    subtitle: 'Optional — we use this in the opening sentence.',
    prompts: [
      "Where did {name} call home most recently — which city and state?",
      "Is there another place the family would like mentioned — maybe where {name} was born or grew up?",
    ],
    fields: [
      { name: 'homeCity',   label: 'Home city',              placeholder: 'Springfield',                   col: 'col-md-6', promptIndex: 1 },
      { name: 'homeState',  label: 'Home state',             placeholder: 'Ohio',                          col: 'col-md-6', promptIndex: 1 },
      { name: 'birthplace', label: 'Birthplace / hometown',  placeholder: 'Born in Cleveland, Ohio; raised on a farm in Medina County', col: 'col-12',  promptIndex: 2 },
    ],
  },
  {
    id: 'life',
    title: 'Tell us about their work and education.',
    subtitle: 'Brief phrases or full sentences both work — the AI will shape them.',
    prompts: [
      "Tell me a little about {name}'s working life. Where did they spend most of their career, and what did they take pride in?",
      "Did {name} attend college, trade school, or any program the family would like mentioned?",
      "Did {name} serve in the military? If so, which branch and roughly when?",
    ],
    fields: [
      { name: 'occupation',      label: 'Occupation / career', placeholder: 'Retired teacher at Springfield High for 30 years', col: 'col-12', promptIndex: 1 },
      { name: 'education',       label: 'Education',           placeholder: 'B.A. in English from Ohio State, 1968',            col: 'col-12', promptIndex: 2 },
      { name: 'militaryService', label: 'Military service',    placeholder: 'U.S. Army, 1965–1968, Sergeant',                   col: 'col-12', promptIndex: 3 },
    ],
  },
  {
    id: 'memories',
    title: 'What did they love?',
    subtitle: 'Hobbies, passions, and notable accomplishments.',
    prompts: [
      "Outside of work, what lit {name} up? What did they love spending their time on — hobbies, clubs, volunteer work, favorite places?",
      "Is there any award or accomplishment the family would want mentioned — something {name} was particularly proud of?",
    ],
    fields: [
      { name: 'hobbies',      label: 'Hobbies & interests',               type: 'textarea', rows: 3, placeholder: 'Gardening, birdwatching, weekly bridge club…',                 col: 'col-12', promptIndex: 1 },
      { name: 'achievements', label: 'Achievements & accomplishments',    type: 'textarea', rows: 3, placeholder: 'Volunteer of the year 2001, published poet, youth softball coach…', col: 'col-12', promptIndex: 2 },
    ],
  },
  {
    id: 'family',
    title: 'Who is family?',
    subtitle: 'Use natural phrases — "spouse John; children Jane and Mark…"',
    prompts: [
      "This next part can be difficult, so please take your time. I'd like to list who {name} leaves behind — we usually start with a spouse or partner, then children (with spouses in parentheses), then grandchildren and great-grandchildren.",
      "Is there anyone who passed before {name} that you'd like us to mention — parents, a spouse, a sibling, or a child?",
    ],
    fields: [
      { name: 'survivedBy', label: 'Survived by',            type: 'textarea', rows: 3, placeholder: 'Spouse John Doe; children Jane (Mike) and Mark; 5 grandchildren', col: 'col-12', promptIndex: 1 },
      { name: 'precededBy', label: 'Preceded in death by',   type: 'textarea', rows: 2, placeholder: 'Parents John and Mary Doe; brother Robert',                       col: 'col-12', promptIndex: 2 },
    ],
  },
  {
    id: 'service',
    title: 'Service details',
    subtitle: 'Leave blank if services are private or TBD.',
    customId: 'service',
    prompts: [
      "Have you decided on a date for the service yet, or would you prefer we say that details will be announced later? Either is perfectly fine — we can always update the obituary.",
      "And the location, if you have it — just the name of the church, funeral home, or venue is fine. You can add the address too if you'd like it listed in the obituary.",
    ],
  },
  {
    id: 'personal',
    title: 'Any final personal touches?',
    subtitle: "Favorite quote or a personal message for the family.",
    prompts: [
      "Did {name} have a favorite saying, a Bible verse, or a phrase that captured who they were?",
      "Is there anything the family would like included word-for-word — a short message, a thank-you, or a line that means something special to them?",
    ],
    fields: [
      { name: 'favoriteQuote',   label: 'Favorite quote or saying', placeholder: '"Always look on the bright side of life."', col: 'col-12', promptIndex: 1 },
      { name: 'personalMessage', label: 'Personal message', type: 'textarea', rows: 4, placeholder: 'Anything the family wants included verbatim.', col: 'col-12', promptIndex: 2 },
    ],
  },
  {
    id: 'review',
    title: 'Ready to generate',
    subtitle: 'Review what we collected. You can edit the draft after generating.',
    review: true,
  },
];

const REVIEW_GROUPS = [
  { label: 'Name', fields: ['firstName', 'middleName', 'lastName', 'nickname'] },
  { label: 'Dates', fields: ['dateOfBirth', 'dateOfDeath', 'age'] },
  { label: 'Home', fields: ['homeCity', 'homeState', 'birthplace'] },
  { label: 'Life', fields: ['occupation', 'education', 'militaryService'] },
  { label: 'Memories', fields: ['hobbies', 'achievements'] },
  { label: 'Family', fields: ['survivedBy', 'precededBy'] },
  { label: 'Service', fields: ['serviceDate', 'serviceLocation'] },
  { label: 'Personal', fields: ['favoriteQuote', 'personalMessage'] },
];

const ObituaryWizard = ({
  onGenerate,
  customSubmit,
  initialFormData,
  loading: externalLoading,
  serviceLocationOptions,
}) => {
  const [formData, setFormData] = useState({ ...EMPTY_DATA, ...(initialFormData || {}) });
  const [stepIndex, setStepIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (initialFormData) {
      setFormData((prev) => ({ ...prev, ...initialFormData }));
    }
  }, [initialFormData]);

  useEffect(() => {
    if (formData.dateOfBirth && formData.dateOfDeath) {
      const birth = new Date(formData.dateOfBirth);
      const death = new Date(formData.dateOfDeath);
      let age = death.getFullYear() - birth.getFullYear();
      const m = death.getMonth() - birth.getMonth();
      const d = death.getDate() - birth.getDate();
      if (m < 0 || (m === 0 && d < 0)) age--;
      if (age >= 0 && age < 150) {
        setFormData((prev) => ({ ...prev, age: String(age) }));
      }
    }
  }, [formData.dateOfBirth, formData.dateOfDeath]);

  const step = STEPS[stepIndex];
  const isLastStep = stepIndex === STEPS.length - 1;
  const canAdvance = useMemo(() => {
    if (!step.required) return true;
    return Boolean(step.required(formData));
  }, [step, formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const next = () => {
    if (!canAdvance) return;
    setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
  };
  const back = () => setStepIndex((i) => Math.max(0, i - 1));
  const jumpTo = (i) => setStepIndex(Math.max(0, Math.min(i, STEPS.length - 1)));

  const handleGenerate = async () => {
    if (typeof customSubmit !== 'function') return;
    setError('');
    setLoading(true);
    try {
      const result = await customSubmit(formData);
      if (onGenerate) {
        const fullName = [formData.firstName, formData.middleName, formData.lastName].filter(Boolean).join(' ');
        onGenerate({
          ...formData,
          obituaryText: result.obituary_text || result.obituaryText,
          fullName,
          credits_remaining: result.credits_remaining,
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to generate obituary.');
    } finally {
      setLoading(false);
    }
  };

  const keyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault();
      if (!isLastStep && canAdvance) next();
    }
  };

  const renderField = (f) => {
    const common = {
      id: `wiz-${f.name}`,
      name: f.name,
      value: formData[f.name] || '',
      onChange: handleChange,
      placeholder: f.placeholder || '',
      disabled: f.disabled || false,
      className: 'form-control',
    };
    return (
      <div key={f.name} className={f.col || 'col-12'}>
        <label htmlFor={common.id} className="form-label">
          {f.promptIndex && (
            <span className="fd-prompt-ref" aria-hidden="true">[{f.promptIndex}]</span>
          )}
          {f.label}
          {f.required && <span className="text-danger ms-1">*</span>}
        </label>
        {f.type === 'textarea' ? (
          <textarea {...common} rows={f.rows || 3} />
        ) : (
          <input {...common} type={f.type || 'text'} />
        )}
      </div>
    );
  };

  // Custom renderer for the "dates" step so we can handle the
  // "unknown" case gracefully: both dates become optional, and when
  // either is blank the Age field becomes editable (and required).
  const renderDatesStep = () => {
    const bothDates = Boolean(formData.dateOfBirth && formData.dateOfDeath);
    return (
      <div className="row g-3">
        <div className="col-md-4">
          <label htmlFor="wiz-dateOfBirth" className="form-label">
            <span className="fd-prompt-ref" aria-hidden="true">[1]</span>
            Date of birth
          </label>
          <input
            type="date"
            id="wiz-dateOfBirth"
            name="dateOfBirth"
            className="form-control"
            value={formData.dateOfBirth || ''}
            onChange={handleChange}
          />
          <small className="form-text fd-wizard-hint">Leave blank if unknown</small>
        </div>
        <div className="col-md-4">
          <label htmlFor="wiz-dateOfDeath" className="form-label">
            <span className="fd-prompt-ref" aria-hidden="true">[2]</span>
            Date of passing
          </label>
          <input
            type="date"
            id="wiz-dateOfDeath"
            name="dateOfDeath"
            className="form-control"
            value={formData.dateOfDeath || ''}
            onChange={handleChange}
          />
          <small className="form-text fd-wizard-hint">Leave blank if unknown</small>
        </div>
        <div className="col-md-4">
          <label htmlFor="wiz-age" className="form-label">
            <span className="fd-prompt-ref" aria-hidden="true">[3]</span>
            Age{!bothDates && <span className="text-danger ms-1">*</span>}
          </label>
          <input
            type="number"
            min="0"
            max="149"
            id="wiz-age"
            name="age"
            className="form-control"
            value={formData.age || ''}
            onChange={handleChange}
            placeholder={bothDates ? 'Auto' : 'Enter age manually'}
            disabled={bothDates}
          />
          <small className="form-text fd-wizard-hint">
            {bothDates ? 'Calculated from the dates above' : 'Required when either date is unknown'}
          </small>
        </div>
      </div>
    );
  };

  // Custom renderer for the "service" step so we can attach a <datalist>
  // of previously-used service locations for quick selection.
  const renderServiceStep = () => {
    const options = Array.isArray(serviceLocationOptions) ? serviceLocationOptions : [];
    const listId = options.length ? 'fd-wiz-service-location-options' : undefined;
    return (
      <div className="row g-3">
        <div className="col-md-6">
          <label htmlFor="wiz-serviceDate" className="form-label">
            <span className="fd-prompt-ref" aria-hidden="true">[1]</span>
            Service date
          </label>
          <input
            type="date"
            id="wiz-serviceDate"
            name="serviceDate"
            className="form-control"
            value={formData.serviceDate || ''}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-6">
          <label htmlFor="wiz-serviceLocation" className="form-label">
            <span className="fd-prompt-ref" aria-hidden="true">[2]</span>
            Service location
          </label>
          <input
            type="text"
            id="wiz-serviceLocation"
            name="serviceLocation"
            className="form-control"
            value={formData.serviceLocation || ''}
            onChange={handleChange}
            placeholder='e.g., "First Baptist Church" (address optional)'
            list={listId}
            autoComplete="off"
          />
          {listId && (
            <datalist id={listId}>
              {options.map((opt) => (
                <option key={opt} value={opt} />
              ))}
            </datalist>
          )}
          {options.length > 0 && (
            <small className="form-text fd-wizard-hint">
              Start typing to pick a saved location, or enter a new one. Manage
              the list under <strong>Service Locations</strong> in the sidebar.
            </small>
          )}
        </div>
      </div>
    );
  };

  // Render the suggested "To the family" prompts for the current step as
  // a numbered list. Each entry corresponds to a field (via promptIndex),
  // so the FD can pause/start between questions.
  const renderFamilyPrompt = () => {
    const prompts = step.prompts && step.prompts.length ? step.prompts : null;
    if (!prompts) return null;
    const name = (formData.firstName || '').trim() || 'your loved one';
    return (
      <div className="fd-family-prompt" role="note" aria-label="Suggested phrasing for the family">
        <div className="fd-family-prompt-label">Suggested phrasing — to the family</div>
        <ol className="fd-family-prompt-list">
          {prompts.map((raw, i) => (
            <li key={i} className="fd-family-prompt-item">
              <span className="fd-family-prompt-num" aria-hidden="true">[{i + 1}]</span>
              <span className="fd-family-prompt-text">
                {raw.replace(/\{name\}/g, name)}
              </span>
            </li>
          ))}
        </ol>
      </div>
    );
  };

  const fmtDate = (v) => {
    if (!v) return '';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return v;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const pretty = (key) => {
    if (key === 'dateOfBirth' || key === 'dateOfDeath' || key === 'serviceDate') return fmtDate(formData[key]);
    return formData[key] || '';
  };

  const labels = {
    firstName: 'First name',
    middleName: 'Middle name',
    lastName: 'Last name',
    nickname: 'Nickname / preferred name',
    dateOfBirth: 'Date of birth',
    dateOfDeath: 'Date of passing',
    age: 'Age',
    homeCity: 'Home city',
    homeState: 'Home state',
    birthplace: 'Birthplace / hometown',
    occupation: 'Occupation',
    education: 'Education',
    militaryService: 'Military service',
    hobbies: 'Hobbies',
    achievements: 'Achievements',
    survivedBy: 'Survived by',
    precededBy: 'Preceded by',
    serviceDate: 'Service date',
    serviceLocation: 'Service location',
    favoriteQuote: 'Favorite quote',
    personalMessage: 'Personal message',
  };

  const progressPct = Math.round(((stepIndex + (isLastStep ? 1 : 0)) / STEPS.length) * 100);
  const busy = loading || externalLoading;

  return (
    <div className="glass-card fd-wizard">
      <div className="fd-wizard-top">
        <div className="fd-wizard-step-count">
          Step {stepIndex + 1} of {STEPS.length}
        </div>
        <div className="fd-wizard-progress" aria-hidden="true">
          <span style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      <ol className="fd-wizard-stepper" aria-label="Wizard progress">
        {STEPS.map((s, i) => (
          <li
            key={s.id}
            className={`fd-wizard-stepper-item ${i === stepIndex ? 'is-active' : ''} ${i < stepIndex ? 'is-done' : ''}`}
          >
            <button
              type="button"
              className="fd-wizard-stepper-btn"
              onClick={() => jumpTo(i)}
              disabled={i > stepIndex && !canAdvance}
              title={s.title}
            >
              <span className="fd-wizard-stepper-dot">{i < stepIndex ? '✓' : i + 1}</span>
            </button>
          </li>
        ))}
      </ol>

      <div className="fd-wizard-body" onKeyDown={keyDown}>
        <h2 className="fd-wizard-title">{step.title}</h2>
        {step.subtitle && <p className="fd-wizard-subtitle">{step.subtitle}</p>}

        {renderFamilyPrompt()}

        {step.review ? (
          <div className="fd-wizard-review">
            {REVIEW_GROUPS.map((g) => {
              const hasAny = g.fields.some((k) => formData[k]);
              if (!hasAny) return null;
              return (
                <div key={g.label} className="fd-wizard-review-group">
                  <div className="fd-wizard-review-group-label">{g.label}</div>
                  <dl className="fd-wizard-review-list">
                    {g.fields.map((k) =>
                      formData[k] ? (
                        <React.Fragment key={k}>
                          <dt>{labels[k]}</dt>
                          <dd>{pretty(k)}</dd>
                        </React.Fragment>
                      ) : null
                    )}
                  </dl>
                </div>
              );
            })}
            {!formData.firstName && !formData.lastName && (
              <div className="alert alert-warning mb-0">
                Please go back and add a name before generating.
              </div>
            )}
          </div>
        ) : step.customId === 'dates' ? (
          renderDatesStep()
        ) : step.customId === 'service' ? (
          renderServiceStep()
        ) : (
          <div className="row g-3">{step.fields.map(renderField)}</div>
        )}

        {error && (
          <div className="alert alert-danger mt-3 mb-0" role="alert">
            {error}
          </div>
        )}
      </div>

      <div className="fd-wizard-actions">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={back}
          disabled={stepIndex === 0 || busy}
        >
          ← Back
        </button>

        <div className="fd-wizard-actions-right">
          {isLastStep && (
            <button
              type="button"
              className="btn btn-lg btn-print-review"
              onClick={() => printObituaryReview(formData)}
              disabled={busy}
              title="Print a review sheet so the family can confirm every detail before you generate the obituary"
            >
              <svg
                className="btn-print-review-icon"
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M6 9V3h12v6" />
                <rect x="3" y="9" width="18" height="9" rx="2" />
                <rect x="6" y="14" width="12" height="7" rx="1" />
                <circle cx="17" cy="12" r="0.6" fill="currentColor" />
              </svg>
              Print for Review
            </button>
          )}

          {isLastStep ? (
            <button
              type="button"
              className="btn btn-primary btn-lg"
              onClick={handleGenerate}
              disabled={busy || !formData.firstName || !formData.lastName}
            >
              {busy ? 'Generating…' : 'Generate obituary'}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary btn-lg"
              onClick={next}
              disabled={!canAdvance || busy}
            >
              Continue →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ObituaryWizard;
